import styled from '@emotion/styled';
import useToast from '@hooks/ui/useToast';
import { ItemResponse } from '@models/item';
import OrderContext from '@providers/OrderProvider/OrderContext';
import { RootState } from '@store/store';
import {
  MouseEvent, ReactNode, useMemo,
} from 'react';
import { useSelector } from 'react-redux';

interface Props {
    children: ReactNode;
    item: ItemResponse;
}

function OptionSelectionGuard({ item, children }:Props) {
  const { orderLines } = useSelector((state: RootState) => state.item);
  const toast = useToast();
  const isPrevent = useMemo(
    () => {
      const { defaultOptionGroups, personalOptionGroups } = item;
      const allOptionGroups = [...defaultOptionGroups, ...personalOptionGroups];
      const selectedOptions = orderLines.map((line) => [...line.defaultOptions, ...line.personalOptions])
        .flat();
      for (const optionGroup of allOptionGroups) {
        const {
          optionGroupId, minSelection, maxSelection, options,
        } = optionGroup;
        const selectedQuantity = selectedOptions
          .filter((og) => og.optionGroupId === optionGroupId)
          .map((og) => og.options).flat()
          .map((option) => option.quantity)
          .reduce((prev, curr) => prev + curr, 0);
        if (selectedQuantity > maxSelection) {
          return true;
        }
        if (selectedQuantity < minSelection && minSelection <= options.length) {
          return true;
        }
      }

      return false;
    },
    [orderLines, item],
  );
  const preventMinOptionCount = (e: MouseEvent) => {
    const { defaultOptionGroups, personalOptionGroups } = item;
    const allOptionGroups = [...defaultOptionGroups, ...personalOptionGroups];

    // 옵션 그룹별로 확인
    for (const optionGroup of allOptionGroups) {
      const {
        optionGroupId, minSelection, maxSelection, name, options,
      } = optionGroup;
      const selectedOptions = orderLines.map((line) => [...line.personalOptions, ...line.defaultOptions])
        .flat()
        .filter((og) => og.optionGroupId === optionGroupId)
        .map((og) => og.options)
        .flat();
      // 선택 수량 체크
      const selectedQuantity = selectedOptions
        .map((option) => option.quantity)
        .reduce((prev, curr) => prev + curr, 0);

      if (selectedQuantity > maxSelection) {
        toast.error(`${name}은 최대 ${maxSelection}개까지 선택 가능합니다.`);
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (selectedQuantity < minSelection && minSelection <= options.length) {
        toast.error(`${name}은 최소 ${minSelection}개 이상 선택해야 합니다.`);
        e.preventDefault();
        e.stopPropagation();
        return;
      }
    }
  };
  return (
        <div onClick={preventMinOptionCount}>
            <Guard isPrevent={isPrevent}>
                {children}
            </Guard>
        </div>
  );
}

const Guard = styled.div<{isPrevent: boolean}>`
    pointer-events: ${(props) => (props.isPrevent ? 'none' : 'auto')};
`;

export default OptionSelectionGuard;
